import type { ReactNode } from "react";
import { cn, type ChatAttachment } from "@drpcrd/utils";
import { Typography } from "../typography";
import { ActivityTabMessage } from "./activity-tab-message";
import { ChatAttachmentLink } from "./chat-attachment-link";

type ChatMessageWithAttachmentProps = {
  /** Own side sits on the right in the primary bubble; the other side on the left. */
  side: "own" | "other";
  text: ReactNode;
  attachment?: ChatAttachment;
  /** Sender, time and seen status. */
  footer: ReactNode;
  /** Extra classes for the column, e.g. a min width as a share of the row. */
  className?: string;
  /** Classes for the footer only when it sits inside the bubble, under the text. */
  bubbleFooterClassName?: string;
};

/**
 * One chat message in an activity feed: the attachment link (if any) above
 * the bubble. A message sent as just a file has no bubble, so its footer
 * sits under the link instead.
 */
export const ChatMessageWithAttachment = ({
  side,
  text,
  attachment,
  footer,
  className,
  bubbleFooterClassName,
}: ChatMessageWithAttachmentProps) => {
  const hasText = typeof text === "string" ? text.trim() !== "" : text != null;

  return (
    <div
      className={cn(
        "flex max-w-[470px] flex-col gap-0.5",
        side === "own" ? "items-end" : "min-w-0 items-start",
        className,
      )}
    >
      {attachment ? (
        <ChatAttachmentLink attachment={attachment} side={side} />
      ) : null}
      {hasText || !attachment ? (
        <ActivityTabMessage
          className="w-full"
          variant={side === "own" ? "primary" : "secondary"}
        >
          {hasText ? (
            <Typography className="message-text" size="sm">
              {text}
            </Typography>
          ) : null}
          <div className={bubbleFooterClassName}>{footer}</div>
        </ActivityTabMessage>
      ) : (
        footer
      )}
    </div>
  );
};
